import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Butterfly, Flower, Info, CircleNotch, Thermometer,
  Sparkle, Sun, Drop, Tree, Plant, Leaf, Grains, CaretDown,
  Star, FlowerLotus,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchRegions, fetchBees } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import { StoryCard, ExplainPanel, MetricTooltip } from "@/components/Explain";
import GlossaryTerm from "@/components/GlossaryTerm";

const ICON_MAP = {
  Flower, Tree, Plant, Leaf, Sun, Grains, FlowerLotus,
};

function StatusChip({ status }) {
  const styles = {
    active:  { bg: "#00FA9A", fg: "#FFFFFF", dot: "Blüht jetzt", pulse: true },
    soon:    { bg: "#FFC24B", fg: "#FFFFFF", dot: "Blüht bald", pulse: false },
    future:  { bg: "rgba(255,255,255,0.06)", fg: "#A1A1AA", dot: "Kommt noch", pulse: false },
    past:    { bg: "rgba(255,255,255,0.10)", fg: "#8A8A93", dot: "Vorbei", pulse: false },
  };
  const s = styles[status] || styles.future;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm font-mono font-semibold"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      {s.pulse && (
        <motion.span
          className="w-1.5 h-1.5 rounded-full bg-[#121212]"
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 1.4 }}
        />
      )}
      {s.dot}
    </span>
  );
}

function StarsRow({ value, label, color }) {
  return (
    <MetricTooltip explain={`${label}: 1 (sehr wenig) bis 5 (Top). Bestimmt, wie wichtig die Pflanze für Bienen ist.`}>
      <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={12}
            weight={i <= value ? "fill" : "regular"}
            style={{ color: i <= value ? color : "#D0CFC8" }}
          />
        ))}
      </span>
    </MetricTooltip>
  );
}

function BloomCard({ w }) {
  const [open, setOpen] = useState(w.status === "active");
  const Icon = ICON_MAP[w.icon] || Flower;
  const isActive = w.status === "active";
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden bg-[#121212] border ${isActive ? "border-[#00FA9A]/40" : "border-white/10"} rounded-sm`}
      data-testid={`bee-window-${w.id}`}
      style={isActive ? { boxShadow: "5px 5px 0 0 rgba(45,90,39,0.15)" } : {}}
    >
      {/* Color-band hero */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left"
        aria-expanded={open}
      >
        <div
          className="px-5 py-5 flex items-start gap-4 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${w.color_secondary}40 0%, #121212 75%)`,
          }}
        >
          <div
            className="shrink-0 w-16 h-16 rounded-sm flex items-center justify-center text-3xl"
            style={{ background: w.color, color: "#FFFFFF" }}
            aria-hidden="true"
          >
            <Icon size={32} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-xl font-bold tracking-tight leading-tight">{w.label}</h3>
              <StatusChip status={w.status} />
            </div>
            <p className="text-sm text-white/70 mt-1 leading-snug">
              {w.label_long}
            </p>
            <div className="mt-2 flex items-center gap-4 flex-wrap text-[11px] font-mono uppercase tracking-widest text-zinc-400">
              <span className="inline-flex items-center gap-1.5">
                <Sparkle size={10} weight="fill" style={{ color: w.color }} /> Nektar <StarsRow value={w.nectar_value} label="Nektar" color={w.color} />
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FlowerLotus size={10} weight="fill" style={{ color: w.color }} /> Pollen <StarsRow value={w.pollen_value} label="Pollen" color={w.color} />
              </span>
            </div>
          </div>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            className="shrink-0 mt-1"
          >
            <CaretDown size={16} weight="bold" className="text-zinc-400" />
          </motion.div>
        </div>
      </button>

      {/* Details */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-5 pb-5 pt-1 space-y-3 border-t border-white/10 text-sm leading-relaxed">
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="text-xs">
                  <div className="font-mono uppercase tracking-widest text-zinc-500 text-[10px]">Art</div>
                  <div className="text-white mt-0.5">{w.kind} · {w.height_m}</div>
                </div>
                <div className="text-xs">
                  <div className="font-mono uppercase tracking-widest text-zinc-500 text-[10px]">Blühzeit</div>
                  <div className="text-white mt-0.5 font-mono">
                    Tag {w.start_doy}–{w.end_doy} · Höhepunkt {w.peak_doy}
                  </div>
                </div>
              </div>
              <div className="bg-[#FFD700]/10 border-l-2 border-[#FFC24B]/40 px-3 py-2.5 rounded-sm text-[13px] text-white" data-testid={`bloom-honey-${w.id}`}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#E8CB6B] mr-2">
                  🍯 Was wird daraus?
                </span>
                {w.honey}
              </div>
              <div className="text-[13px]" data-testid={`bloom-pollinators-${w.id}`}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400 mr-2">
                  🐝 Wer besucht?
                </span>
                <span className="text-white">{(w.pollinators || []).join(" · ")}</span>
              </div>
              <div className="bg-[#8B7CF6]/10 border-l-2 border-[#8B7CF6]/40 px-3 py-2.5 rounded-sm text-[13px] text-white italic" data-testid={`bloom-fact-${w.id}`}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono not-italic uppercase tracking-widest text-[#A99AFF] mr-2">
                  ✨ Wusstest du?
                </span>
                {w.fun_fact}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export default function Bees() {
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRegions().then((rs) => {
      setRegions(rs);
      if (!regionId && rs.length) setRegionId(rs[0].id);
    }).catch(() => setRegions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!regionId) return;
    setLoading(true);
    fetchBees(regionId)
      .then(setData)
      .catch(() => { setData(null); toast.error("Konnte Bienenkalender nicht laden"); })
      .finally(() => setLoading(false));
  }, [regionId]);

  // Group blooms by status for clearer reading
  const grouped = useMemo(() => {
    const buckets = { active: [], soon: [], future: [], past: [] };
    (data?.windows || []).forEach((w) => {
      (buckets[w.status] || buckets.future).push(w);
    });
    return buckets;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="bees-page">
      <Helmet><title>Bienen-Kalender · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8 relative overflow-hidden" data-testid="bees-hero"
        style={{ background: "linear-gradient(135deg, #1A160A 0%, #0E1A14 100%)" }}>
        <div className="flex items-center gap-2">
          <Butterfly size={20} className="text-[#00FA9A]" weight="fill" />
          <div className="eid-label">Bienen & Blüten</div>
        </div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-bold tracking-tighter max-w-3xl leading-[0.95]">
          Wann blüht's wo? Der Bodenblick-Bienen-Kalender.
        </h1>
        <p className="mt-4 text-sm md:text-base text-white/75 max-w-2xl leading-relaxed">
          Für Imker:innen, Landwirt:innen und alle, die Bestäubung verstehen wollen.
          Wir zeigen dir Blüh-Fenster der wichtigsten Nektar- und Pollenquellen — und
          ob heute überhaupt <GlossaryTerm word="Bestäuber-Tag">Bienenwetter</GlossaryTerm> herrscht.
        </p>
      </div>

      <StoryCard
        eyebrow="So liest du den Bienen-Kalender"
        title="Jede Karte zeigt eine Blühpflanze."
        lead="Aufklappen für: Pflanzenart, Höhe, Honigsorte, welche Bestäuber sie besuchen — und eine spannende Tatsache, die du im Schullbuch nicht findest."
        bullets={[
          "Grüner Status = blüht JETZT in deiner Region",
          "Sterne = Nektar- und Pollen-Wert von 1 (kaum) bis 5 (Top-Tracht)",
          "Was wird daraus? — die typische Honigsorte pro Pflanze",
        ]}
        accent="#FFC24B"
        icon={Sparkle}
        testid="bees-story"
      />

      <ExplainPanel
        title="Was bedeutet ‚Bienenwetter'?"
        accent="#FFC24B"
        testid="bees-explain"
      >
        <p>
          Honig- und Wildbienen verlassen den Stock erst <strong>ab etwa 12 °C</strong>. Darunter bleibt der Flugverkehr eingeschlossen, selbst wenn die Sonne scheint. Bodenblick prüft die aktuelle Temperatur an deiner gewählten Region und zeigt dir, ob die Bienen heute überhaupt unterwegs sein können. Mehr dazu unter <GlossaryTerm word="Bestäuber-Tag">Bestäuber-Tag</GlossaryTerm>.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-1">
          <div className="border border-[#FFC24B]/40 p-3 rounded-sm bg-black/50">
            <Thermometer size={16} className="text-[#FFC24B]" weight="fill" />
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mt-1">Temperatur</div>
            <div className="text-sm text-white mt-0.5">Aktueller Wert · Live aus Open-Meteo</div>
          </div>
          <div className="border border-[#FFC24B]/40 p-3 rounded-sm bg-black/50">
            <Sun size={16} className="text-[#FFC24B]" weight="fill" />
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mt-1">Sonne</div>
            <div className="text-sm text-white mt-0.5">Mehr Sonne = aktivere Flugphase</div>
          </div>
          <div className="border border-[#FFC24B]/40 p-3 rounded-sm bg-black/50">
            <Drop size={16} className="text-[#FFC24B]" weight="fill" />
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mt-1">Regen</div>
            <div className="text-sm text-white mt-0.5">Über 1 mm/h: Bienen warten im Stock</div>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-2 pt-2 border-t border-white/15">
          Tip für Imker:innen: Notiere dir die Höhepunkt-Tage der wichtigsten Trachten —
          Raps, Linde, Robinie, Phacelia — und plane Honig-Ernten 2–3 Tage danach.
        </p>
      </ExplainPanel>

      <div className="eid-card p-4 flex flex-wrap items-center gap-3" data-testid="bees-picker">
        <div className="eid-label">Region</div>
        <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="bees-region-select" />
      </div>

      {loading && (
        <div className="eid-card p-6 flex items-center gap-2" data-testid="bees-loading">
          <CircleNotch size={16} className="animate-spin" /> Zähle Blüten …
        </div>
      )}

      {data && !loading && (
        <>
          <div
            className="eid-card p-5 flex items-start gap-3"
            data-testid="bees-headline"
            style={{
              borderLeft: `4px solid ${data.flight_conditions_ok ? "#00FA9A" : "#FFC24B"}`,
            }}
          >
            <Flower size={22} className="text-[#00FA9A] shrink-0 mt-0.5" weight="fill" />
            <div className="flex-1">
              <div className="text-lg font-medium">{data.headline}</div>
              <div className="text-sm text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Thermometer size={12} />
                  {data.current_temperature_c != null
                    ? `${data.current_temperature_c.toFixed(1)} °C`
                    : "Keine Temperatur"}
                </span>
                <span>·</span>
                <span>
                  {data.flight_conditions_ok
                    ? "🐝 Bienenwetter (≥ 12 °C)"
                    : "⚠️ Zu kalt für Flug (< 12 °C)"}
                </span>
              </div>
            </div>
          </div>

          {/* Currently flowering */}
          {grouped.active.length > 0 && (
            <section data-testid="bees-active-section">
              <h2 className="text-sm font-mono uppercase tracking-[0.22em] text-[#00FA9A] mb-3 flex items-center gap-2">
                <motion.span
                  className="w-1.5 h-1.5 rounded-full bg-[#00FA9A]"
                  animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
                  transition={{ repeat: Infinity, duration: 1.4 }}
                />
                Jetzt am Blühen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped.active.map((w) => <BloomCard key={w.id} w={w} />)}
              </div>
            </section>
          )}

          {/* Coming soon */}
          {grouped.soon.length > 0 && (
            <section data-testid="bees-soon-section">
              <h2 className="text-sm font-mono uppercase tracking-[0.22em] text-[#FFC24B] mb-3">
                Kommt bald
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped.soon.map((w) => <BloomCard key={w.id} w={w} />)}
              </div>
            </section>
          )}

          {/* Future */}
          {grouped.future.length > 0 && (
            <section data-testid="bees-future-section">
              <h2 className="text-sm font-mono uppercase tracking-[0.22em] text-zinc-400 mb-3">
                Später im Jahr
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped.future.map((w) => <BloomCard key={w.id} w={w} />)}
              </div>
            </section>
          )}

          {/* Past */}
          {grouped.past.length > 0 && (
            <section data-testid="bees-past-section">
              <h2 className="text-sm font-mono uppercase tracking-[0.22em] text-zinc-500 mb-3">
                In diesem Jahr schon durch
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped.past.map((w) => <BloomCard key={w.id} w={w} />)}
              </div>
            </section>
          )}

          <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
            <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
            <span>{data.hint}</span>
          </div>
        </>
      )}
    </div>
  );
}
